import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

type GalleryWithPaginationProps = {
  items: Array<{
    id: number;
    title: string;
    slug: string;
    description: string;
    imageUrl: string;
    location?: string;
    floors?: string;
    properties?: string;
  }>;
  itemsPerPage: number;
};

const AdvancedGallery: React.FC<GalleryWithPaginationProps> = ({
  items,
  itemsPerPage,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const navigate = useNavigate();

  const totalPages = Math.ceil(items.length / itemsPerPage);
  const paginatedItems = items.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setActiveCardIndex(0);
  };

  const handleCardClick = (index: number) => {
    if (index === activeCardIndex) {
      navigate(`/developments/${paginatedItems[index].slug}`);
    } else {
      setActiveCardIndex(index);
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex h-[42rem] mb-[2.4rem] gap-4 overflow-hidden">
        {paginatedItems.map((item, index) => (
          <motion.div
            key={item.id}
            layout
            className={`relative overflow-hidden rounded-lg shadow-lg cursor-pointer ${
              index === activeCardIndex ? "flex-grow" : "flex-shrink"
            }`}
            style={{
              flexBasis: index === activeCardIndex ? "50%" : "16%",
              maxHeight: "42rem",
              height: "100%",
              transition: "flex-basis 0.5s ease",
            }}
            onClick={() => handleCardClick(index)}
          >
            <div className="relative w-full h-full">
              <img
                src={item.imageUrl}
                alt={item.title}
                className="absolute inset-0 w-full h-full object-cover"
                style={{
                  objectFit: "cover",
                  maxWidth: "100%",
                  maxHeight: "100%",
                }}
              />
            </div>
            {index === activeCardIndex && (
              <motion.div
                className="absolute inset-x-0 bottom-0 bg-[rgba(0, 0, 0, 0.10)] backdrop-blur-md text-white p-[2.4rem] border border-transparent rounded-[0.5rem]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <h6 className="mb-[0.8rem] text-[2rem]/[2rem] text-white">
                  {item.title}
                </h6>
                <p className="mb-[0.8rem]">{item.description}</p>
                {item.properties && (
                  <div className="flex flex-wrap gap-2">
                    {item.properties
                      .split(",")
                      .map((property) => property.trim())
                      .map((property, i) => (
                        <span
                          key={i}
                          style={{ background: "rgba(255, 255, 255, 0.10)" }}
                          className="text-white text-[1.4rem] p-[0.8rem] rounded-[0.5rem] whitespace-nowrap"
                        >
                          {property}
                        </span>
                      ))}
                  </div>
                )}
              </motion.div>
            )}
          </motion.div>
        ))}
      </div>
      {items.length > 4 && (
        <div className="flex justify-between items-center gap-4">
          <p className="text-black">
            Page {currentPage} of {totalPages}
          </p>

          <div className="flex">
            {currentPage !== 1 && (
              <button
                className="mr-[1.2rem]"
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 40 40"
                  fill="none"
                >
                  <rect
                    x="40"
                    y="40"
                    width="40"
                    height="40"
                    rx="5"
                    transform="rotate(-180 40 40)"
                    fill="white"
                  />
                  <path
                    d="M25 10L15 20L25 30"
                    stroke="black"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
            {currentPage !== totalPages && (
              <button
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 40 40"
                  fill="none"
                >
                  <rect width="40" height="40" rx="5" fill="white" />
                  <path
                    d="M15 30L25 20L15 10"
                    stroke="black"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedGallery;
